import { Link } from "react-router-dom";
import type { Testimonial, UnlinkTestimonialType } from "../../../../api/pipeline.service";
import { RESEARCH_ID_FIELDS, hasResearchIds } from "../../../../utils/researchIds";
import { getLanguageFlag } from "../../ContentInbox/utils";
import { UnlinkLawyerButton } from "../../ContentInbox/components/UnlinkLawyerButton";
import styles from "./DetailsColumn.module.css";

type DetailsColumnProps = {
  item: Testimonial;
  onLinkContact?: () => void;
  onUnlinkLawyer?: (testimonialType: UnlinkTestimonialType | undefined) => Promise<void>;
  unlinkDisabled?: boolean;
};

export function DetailsColumn({
  item,
  onLinkContact,
  onUnlinkLawyer,
  unlinkDisabled,
}: DetailsColumnProps) {
  const pubName = item.publicationName ?? item.publicationId ?? "—";
  const fName = item.firmName ? `${item.firmName} (${item.firmRef})` : item.firmRef;
  const pracName = item.practiceAreaName ?? item.practiceAreaId ?? "—";

  return (
    <div className={styles.detailsColumn}>
      <div className={styles.detailsContent}>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Publication:</span>
          <span className={styles.detailValue}>
            {pubName} ({item.edition})
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Firm:</span>
          <span className={styles.detailValue}>{fName}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Region:</span>
          <span className={styles.detailValue}>{item.regionName || "—"}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Practice:</span>
          <span className={styles.detailValue}>{pracName}</span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Lawyer:</span>
          <span className={styles.detailValue}>
            {item.lawyerName ? (
              <>
                {item.lawyerName}
                {item.contactId != null && ` (${item.contactId})`}
                {item.contactId == null && (
                  <button
                    type="button"
                    className={styles.brokenLinkButton}
                    title="Lawyer not linked to a contact in the system — click to fix"
                    onClick={e => {
                      e.stopPropagation();
                      onLinkContact?.();
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="14"
                      height="14"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
                      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
                      <line x1="8" y1="12" x2="16" y2="12" />
                      <line x1="2" y1="2" x2="22" y2="22" />
                    </svg>
                  </button>
                )}
              </>
            ) : (
              "—"
            )}
            {onUnlinkLawyer && (
              <UnlinkLawyerButton
                currentType={item.testimonialType}
                disabled={unlinkDisabled}
                onConfirm={onUnlinkLawyer}
              />
            )}
          </span>
        </div>
        <div className={styles.detailRow}>
          <span className={styles.detailLabel}>Language:</span>
          <span className={styles.detailValue}>{getLanguageFlag(item.language)}</span>
        </div>
        {hasResearchIds(item) ? (
          <details className={styles.researchIds} onClick={e => e.stopPropagation()}>
            <summary>Research ids</summary>
            {RESEARCH_ID_FIELDS.map(({ key, label }) => (
              <div key={key} className={styles.detailRow}>
                <span className={styles.detailLabel}>{label}:</span>
                <span className={`${styles.detailValue} ${styles.researchIdValue}`}>
                  {item[key] ?? "—"}
                </span>
              </div>
            ))}
          </details>
        ) : (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>Research ids:</span>
            <span className={styles.detailValue}>—</span>
          </div>
        )}
        {item.relatedTestimonials && item.relatedTestimonials.length > 0 && (
          <div className={styles.detailRow}>
            <span className={styles.detailLabel}>
              {item.isTranslation ? "Source:" : "Translation:"}
            </span>
            <span className={styles.detailValue}>
              {item.relatedTestimonials.map(rel => (
                <Link
                  key={rel.id}
                  className={styles.relatedLink}
                  to={`/research/testimonials/${rel.id}`}
                  onClick={e => e.stopPropagation()}
                >
                  {getLanguageFlag(rel.language)} Link
                </Link>
              ))}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
